/**
 * Command Palette Component
 * Phase 2: Product Shell & Navigation
 * 
 * Quick navigation via Cmd/Ctrl+K keyboard shortcut.
 * Client-side only - no server search integration.
 */

import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "./Icon";
import { ROUTES, type RouteConfig } from "../lib/navigation";
import { useLocale } from "../contexts/LocaleContext";

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
}

export function CommandPalette({ isOpen, onClose }: CommandPaletteProps) {
  const navigate = useNavigate();
  const { t } = useLocale();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Get all routes (both implemented and placeholder)
  const allRoutes = Object.values(ROUTES).filter(
    (route) =>
      route.implemented &&
      !route.path.includes(":") &&
      route.path !== "/" &&
      route.path !== "/status"
  );

  // Filter routes based on search query
  const filteredRoutes = searchQuery.trim()
    ? allRoutes.filter((route) =>
        route.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
        t(route.labelKey).toLowerCase().includes(searchQuery.toLowerCase()) ||
        route.path.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : allRoutes;

  // Reset selection when filtered results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [searchQuery]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
      setSearchQuery("");
      setSelectedIndex(0);
    }
  }, [isOpen]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((prev) => Math.min(prev + 1, filteredRoutes.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, 0));
      } else if (e.key === "Enter" && filteredRoutes[selectedIndex]) {
        e.preventDefault();
        handleSelectRoute(filteredRoutes[selectedIndex]);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, selectedIndex, filteredRoutes, onClose]);

  // Scroll selected item into view
  useEffect(() => {
    if (listRef.current) {
      const selectedElement = listRef.current.querySelector(`[data-index="${selectedIndex}"]`);
      selectedElement?.scrollIntoView({ block: "nearest" });
    }
  }, [selectedIndex]);

  const handleSelectRoute = (route: RouteConfig) => {
    if (route.implemented) {
      navigate(route.path);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="command-palette-backdrop"
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          backgroundColor: "rgba(0, 0, 0, 0.7)",
          zIndex: 9998,
          backdropFilter: "blur(2px)",
        }}
      />

      {/* Command Palette */}
      <div
        className="command-palette"
        role="dialog"
        aria-label={t('cmd.aria')}
        style={{
          position: "fixed",
          top: "20%",
          left: "50%",
          transform: "translateX(-50%)",
          width: "90%",
          maxWidth: "600px",
          backgroundColor: "var(--reviewer-bg-primary)",
          border: "1px solid var(--reviewer-border)",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.5)",
          zIndex: 9999,
          display: "flex",
          flexDirection: "column",
          maxHeight: "60vh",
        }}
      >
        {/* Search Input */}
        <div
          style={{
            padding: "16px 20px",
            borderBottom: "1px solid var(--reviewer-border)",
            display: "flex",
            alignItems: "center",
            gap: "12px",
          }}
        >
          <Icon name="search" size={20} style={{ color: "var(--reviewer-text-tertiary)" }} />
          <input
            ref={inputRef}
            type="text"
            placeholder={t("common.search")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              flex: 1,
              background: "none",
              border: "none",
              outline: "none",
              color: "var(--reviewer-text-primary)",
              fontSize: "16px",
              fontFamily: "var(--reviewer-font-mono)",
            }}
          />
          <kbd
            style={{
              padding: "4px 8px",
              border: "1px solid var(--reviewer-border)",
              fontSize: "12px",
              color: "var(--reviewer-text-tertiary)",
              fontFamily: "var(--reviewer-font-mono)",
            }}
          >
            ESC
          </kbd>
        </div>

        {/* Results List */}
        <div
          ref={listRef}
          className="command-palette-results"
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "8px",
          }}
        >
          {filteredRoutes.length === 0 ? (
            <div
              style={{
                padding: "32px 20px",
                textAlign: "center",
                color: "var(--reviewer-text-tertiary)",
                fontSize: "14px",
                fontFamily: "var(--reviewer-font-mono)",
              }}
            >
              {t('cmd.noResults')}
            </div>
          ) : (
            filteredRoutes.map((route, index) => (
              <div
                key={route.path}
                data-index={index}
                onClick={() => handleSelectRoute(route)}
                className="command-palette-item"
                style={{
                  padding: "12px 16px",
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  cursor: route.implemented ? "pointer" : "default",
                  backgroundColor:
                    index === selectedIndex ? "var(--reviewer-bg-secondary)" : "transparent",
                  borderLeft:
                    index === selectedIndex ? "3px solid var(--reviewer-accent)" : "3px solid transparent",
                  marginLeft: "-3px",
                  color: route.implemented
                    ? "var(--reviewer-text-primary)"
                    : "var(--reviewer-text-tertiary)",
                  opacity: route.implemented ? 1 : 0.6,
                }}
              >
                <Icon name={route.icon as any} size={18} />
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: 500,
                      fontFamily: "var(--reviewer-font-mono)",
                    }}
                  >
                    {t(route.labelKey)}
                  </div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "var(--reviewer-text-tertiary)",
                      fontFamily: "var(--reviewer-font-mono)",
                    }}
                  >
                    {route.path}
                  </div>
                </div>
                {!route.implemented && (
                  <span
                    style={{
                      fontSize: "11px",
                      padding: "2px 6px",
                      border: "1px solid var(--reviewer-border)",
                      color: "var(--reviewer-text-tertiary)",
                      fontFamily: "var(--reviewer-font-mono)",
                    }}
                  >
                    {t('cmd.unavailable')}
                  </span>
                )}
              </div>
            ))
          )}
        </div>

        {/* Footer Hint */}
        <div
          style={{
            padding: "12px 20px",
            borderTop: "1px solid var(--reviewer-border)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            fontSize: "11px",
            color: "var(--reviewer-text-tertiary)",
            fontFamily: "var(--reviewer-font-mono)",
          }}
        >
          <div style={{ display: "flex", gap: "16px" }}>
            <span>
              <kbd
                style={{
                  padding: "2px 6px",
                  border: "1px solid var(--reviewer-border)",
                  marginRight: "4px",
                }}
              >
                ↑↓
              </kbd>
              {t('cmd.navigate')}
            </span>
            <span>
              <kbd
                style={{
                  padding: "2px 6px",
                  border: "1px solid var(--reviewer-border)",
                  marginRight: "4px",
                }}
              >
                Enter
              </kbd>
              {t('cmd.select')}
            </span>
          </div>
        </div>
      </div>
    </>
  );
}

// ============================================================================
// Command Palette Hook
// ============================================================================

/**
 * Hook to manage command palette state and keyboard shortcut
 */
export function useCommandPalette() {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd/Ctrl + K (handle both lowercase and uppercase)
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setIsOpen(true);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  return {
    isOpen,
    open: () => setIsOpen(true),
    close: () => setIsOpen(false),
  };
}
